/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { ContactPreview } from './Components/ContactPreview';
import { FeaturePreview } from './Components/FeaturePreview';
import { StudioPreview } from './Components/StudioPreview';
import { ContractPreview } from './Components/ContractPreview';
import { ContributionPreview } from './Components/ContributionPreview';
import { AdditionalCostsPreview } from './Components/AdditionalCostsPreview';
import { VoucherCode } from './Components/VoucherCode';
import { ReferralCode } from './Components/ReferralCode';
import { ContractApproval } from './Components/ContractApproval';
import { usePreviewForm } from './usePreviewForm';
import { PreviewFormSkeleton } from './Skeleton/PreviewFormSkeleton';
import { ContractVolumeInformationPreview } from './Components/ContractVolumeInformationPreview';

/**
 * PreviewForm Component
 * @returns {JSX.Element}
 * @constructor
 */
export function PreviewForm(props) {
  const {
    formField: { voucherCode, referralCode },
  } = props;
  const { t } = useTranslation();
  const {
    values: { rateBundle, rateBundleTerm },
  } = useFormikContext();
  const {
    previewData, updatePreviewData, isLoading, isVoucherValid,
  } = usePreviewForm();

  const renderPreviewData = () => {
    if (isLoading) {
      return <PreviewFormSkeleton />;
    }

    return (
      <>
        <Typography mt={4} textAlign="center" variant="h2">
          5.
          {' '}
          {t('stepFiveHeader')}
        </Typography>
        <Typography sx={{ textAlign: 'center' }} variant="h4">
          {t('stepFiveSubHeadline')}
        </Typography>
        <Grid container mt={4} gap={2} sx={{ justifyContent: 'center' }}>
          <Grid
            item
            border="2px solid #49ad33"
            sx={{ backgroundColor: '#222' }}
            mb={{
              xs: '80px',
              sm: 0,
            }}
            xs={12}
            md={8}
          >
            <Box
              p={{
                xs: 2,
                sm: 4,
              }}
            >
              <Typography variant="h2">{rateBundle?.name}</Typography>
              <Typography variant="h4">{`${rateBundleTerm?.termValue} ${t('months')}`}</Typography>
              <ContactPreview />
              <FeaturePreview />
              <StudioPreview />
              <ContractPreview />
              <ContributionPreview />
              <AdditionalCostsPreview />
              <ContractVolumeInformationPreview />
              <VoucherCode voucherCode={voucherCode} updatePreviewData={updatePreviewData} />
              {previewData.value?.voucherSuccessMessage && isVoucherValid.value ? (
                <Box mt={2}>{previewData.value.voucherSuccessMessage}</Box>
              ) : null}
              <ReferralCode referralCode={referralCode} />
              <ContractApproval />
            </Box>
          </Grid>
        </Grid>
      </>
    );
  };

  return <>{renderPreviewData()}</>;
}

PreviewForm.propTypes = {
  formField: PropTypes.object.isRequired,
};
